import { Goal, GoalFormData, GoalCategory, GoalProgressHistory } from '../types/Goal';
import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}


export interface CreateGoalData {
  user_id: string;
  title: string;
  description: string;
  category: string;
  target_date?: string;
  started_date?: string;
  notes?: string;
  attachments?: any[];
  metric_name?: string;
  target_metric_value?: number;
}

export interface UpdateGoalData {
  id: string;
  title?: string;
  description?: string;
  category?: string;
  status?: string;
  progress_percentage?: number;
  target_date?: string | null;
  started_date?: string | null;
  notes?: string;
  attachments?: any[];
  metric_name?: string;
  starting_metric_value?: number;
  target_metric_value?: number;
}

export interface GoalFilters {
  user_id: string;
  status?: string;
  category?: string | GoalCategory;
  search_term?: string;
  include_children?: boolean;
  limit?: number;
  offset?: number;
}

class GoalService {
  // Create a new goal
  async createGoal(userId: string, goalData: GoalFormData): Promise<{ success: boolean; message?: string; id?: number }> {
    try {
      const data: CreateGoalData = {
        user_id: userId,
        title: goalData.title,
        description: goalData.description,
        category: goalData.category,
        target_date: goalData.targetDate || undefined,
        started_date: goalData.startedDate || undefined,
        notes: goalData.notes || undefined,
        attachments: []
      };

      const response = await makeAuthenticatedRequest('/goals', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating goal:', error);
      throw error;
    }
  }

  // Get goal by ID
  async getGoalById(goalId: string): Promise<{ success: boolean; goal?: any }> {
    try {
      const response = await makeAuthenticatedRequest(`/goals/${goalId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching goal:', error);
      throw error;
    }
  }

  // Get all goals for a user
  async getUserGoals(filters: GoalFilters): Promise<{ success: boolean; goals?: any[]; count?: number; children?: any[] }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.search_term) queryParams.append('search_term', filters.search_term);
      if (filters.include_children) queryParams.append('include_children', 'true');
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/goals/user/${filters.user_id}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching user goals:', error);
      throw error;
    }
  }

  // Update a goal
  async updateGoal(updateData: UpdateGoalData): Promise<{ success: boolean; message?: string }> {
    try {
      const { id, ...dataToUpdate } = updateData;
      
      const response = await makeAuthenticatedRequest(`/goals/${id}`, {
        method: 'PUT',
        body: JSON.stringify(dataToUpdate)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating goal:', error);
      throw error;
    }
  }

  // Delete a goal
  async deleteGoal(goalId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/goals/${goalId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting goal:', error);
      throw error;
    }
  }

  // Update goal progress
  async updateGoalProgress(goalId: string, progressPercentage: number): Promise<{ success: boolean; message?: string; completed?: boolean }> {
    try {
      const response = await makeAuthenticatedRequest(`/goals/${goalId}/progress`, {
        method: 'PATCH',
        body: JSON.stringify({
          progress_percentage: progressPercentage
        })
      });
      
      return response;
    } catch (error) {
      console.error('Error updating goal progress:', error);
      throw error;
    }
  }

  // Search goals
  async searchGoals(filters: GoalFilters): Promise<{ success: boolean; goals?: any[]; count?: number }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', filters.user_id);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.search_term) queryParams.append('search_term', filters.search_term);
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      
      const response = await makeAuthenticatedRequest(`/goals/search?${queryParams.toString()}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error searching goals:', error);
      throw error;
    }
  }

  // Get hierarchical goal structure for a user
  async getGoalHierarchy(userId: string, status?: string, category?: string): Promise<{ success: boolean; hierarchy?: any[]; count?: number }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (status) queryParams.append('status', status);
      if (category) queryParams.append('category', category);
      
      const queryString = queryParams.toString();
      const url = `/goals/hierarchy/${userId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching goal hierarchy:', error);
      throw error;
    }
  }

  // Get child goals for a specific goal
  async getChildGoals(goalId: string, status?: string, category?: string, limit?: number, offset?: number): Promise<{ success: boolean; children?: any[]; count?: number }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (status) queryParams.append('status', status);
      if (category) queryParams.append('category', category);
      if (limit) queryParams.append('limit', limit.toString());
      if (offset) queryParams.append('offset', offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/goals/${goalId}/children${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching child goals:', error);
      throw error;
    }
  }

  // Get root goals (goals without parents)
  async getRootGoals(userId: string, filters?: Omit<GoalFilters, 'user_id'>): Promise<{ success: boolean; goals?: any[]; count?: number }> {
    try {
      const rootFilters: GoalFilters = {
        user_id: userId,
        ...filters
      };
      
      return await this.getUserGoals(rootFilters);
    } catch (error) {
      console.error('Error fetching root goals:', error);
      throw error;
    }
  }

  // Get sub-goals (goals with a specific parent) - uses relationships
  async getSubGoals(userId: string, parentGoalId: number, filters?: Omit<GoalFilters, 'user_id'>): Promise<{ success: boolean; goals?: any[]; count?: number }> {
    try {
      // Use the children endpoint which now uses relationships
      const response = await this.getChildGoals(parentGoalId.toString(), filters?.status, filters?.category, filters?.limit, filters?.offset);
      
      // Convert children to goals format for consistency
      return {
        success: response.success,
        goals: response.children || [],
        count: response.count || 0
      };
    } catch (error) {
      console.error('Error fetching sub-goals:', error);
      throw error;
    }
  }

  // Unlink a sub-goal from a parent goal
  async unlinkSubGoal(parentGoalId: string, childGoalId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/goals/${parentGoalId}/children/${childGoalId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error unlinking sub-goal:', error);
      throw error;
    }
  }

  // Calculate goal progress based on linked habits and tasks
  async calculateGoalProgress(goalId: string): Promise<{ success: boolean; progress?: any; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/goals/${goalId}/calculate-progress`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error calculating goal progress:', error);
      throw error;
    }
  }

  // Convert Firebase goal to our Goal interface
  convertFirebaseGoal(firebaseGoal: any): Goal {
    return {
      id: firebaseGoal.id,
      title: firebaseGoal.title,
      description: firebaseGoal.description,
      category: firebaseGoal.category,
      status: firebaseGoal.status,
      targetDate: firebaseGoal.target_date,
      startedDate: firebaseGoal.started_date,
      createdDate: firebaseGoal.created_date,
      completedDate: firebaseGoal.completed_date,
      notes: firebaseGoal.notes,
      progress: typeof firebaseGoal.progress_percentage === 'string' ? parseFloat(firebaseGoal.progress_percentage) : (firebaseGoal.progress_percentage || 0),
      milestones: [], // Milestones will be handled separately if needed
      metric_name: firebaseGoal.metric_name,
      starting_metric_value: firebaseGoal.starting_metric_value,
      target_metric_value: firebaseGoal.target_metric_value
    };
  }

  // Update goal metric values
  async updateGoalMetrics(goalId: string, metricName: string, startingValue: number, targetValue?: number): Promise<{ success: boolean; message?: string }> {
    try {
      const updateData: UpdateGoalData = {
        id: goalId,
        metric_name: metricName,
        starting_metric_value: startingValue
      };

      if (targetValue !== undefined) {
        updateData.target_metric_value = targetValue;
      }

      const response = await this.updateGoal(updateData);
      return response;
    } catch (error) {
      console.error('Error updating goal metrics:', error);
      throw error;
    }
  }

  // Update only the starting metric value for a goal
  async updateStartingMetricValue(goalId: string, startingValue: number): Promise<{ success: boolean; message?: string }> {
    try {
      const updateData: UpdateGoalData = {
        id: goalId,
        starting_metric_value: startingValue
      };

      const response = await this.updateGoal(updateData);
      return response;
    } catch (error) {
      console.error('Error updating starting metric value:', error);
      throw error;
    }
  }

  // Calculate progress based on metric values
  calculateMetricProgress(currentValue: number, targetValue: number): number {
    if (targetValue === 0) return 0;
    const progress = (currentValue / targetValue) * 100;
    return Math.min(Math.max(Math.round(progress * 100) / 100, 0), 100); // Clamp between 0-100
  }

  // Check if goal should auto-complete based on metric
  shouldAutoComplete(currentValue: number, targetValue: number): boolean {
    return currentValue >= targetValue;
  }

  // Update progress for all goals
  async updateAllGoalsProgress(): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/goals/update-progress', {
        method: 'POST'
      });
      
      return response;
    } catch (error) {
      console.error('Error updating all goals progress:', error);
      throw error;
    }
  }

  // Get goal progress history
  async getGoalProgressHistory(
    goalId: string, 
    options?: { limit?: number; startDate?: string; endDate?: string }
  ): Promise<{ success: boolean; history?: GoalProgressHistory[]; message?: string }> {
    try {
      const queryParams = new URLSearchParams();
      if (options?.limit) queryParams.append('limit', options.limit.toString());
      if (options?.startDate) queryParams.append('start_date', options.startDate);
      if (options?.endDate) queryParams.append('end_date', options.endDate);
      
      const queryString = queryParams.toString();
      const url = `/goals/${goalId}/progress-history${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching goal progress history:', error);
      throw error;
    }
  }
}

export const goalService = new GoalService();
